"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { signIn, confirmNewPassword, requestPasswordReset, confirmPasswordReset, clearAuth } from "@/lib/auth";
import { recordLogin } from "@/lib/api";
import { useAuth } from "@/components/auth-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";

type View = 'login' | 'reset_request' | 'reset_confirm';

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [requiresNewPassword, setRequiresNewPassword] = useState(false);
  const [view, setView] = useState<View>('login');
  const [resetUsername, setResetUsername] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [resetNewPassword, setResetNewPassword] = useState("");
  const router = useRouter();
  const { checkAuth } = useAuth();

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (requiresNewPassword) {
        await confirmNewPassword(newPassword);
        // confirmNewPassword成功でセッションが確立済みのため再signIn不要
        // リダイレクト前にユーザー種別をチェック（許可されない種別はログインさせない）
        const { allowed } = await recordLogin();
        if (!allowed) {
          await clearAuth();
          setError('このアカウントではログインできません。');
          return;
        }
        await checkAuth();
        router.push('/');
      } else {
        const result = await signIn(username, password);
        if (result.isSignedIn) {
          // リダイレクト前にユーザー種別をチェック（許可されない種別はログインさせない）
          const { allowed } = await recordLogin();
          if (!allowed) {
            await clearAuth();
            setError('このアカウントではログインできません。');
            return;
          }
          await checkAuth();
          router.push('/');
        } else if ('nextStep' in result && result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          setRequiresNewPassword(true);
        } else {
          setError("ログインIDまたはパスワードが正しくありません。");
        }
      }
    } catch (err: any) {
      setError(err.message || "予期せぬエラーが発生しました。");
      if (err.name === 'UserNotConfirmedException') {
        setError("ユーザーが確認されていません。");
      } else if (err.name === 'PasswordResetRequiredException') {
        setError("パスワードのリセットが必要です。");
      } else if (err.name === 'NotAuthorizedException') {
        setError("ログインIDまたはパスワードが正しくありません。");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await requestPasswordReset(resetUsername);
      setView('reset_confirm');
    } catch (err: any) {
      // セキュリティのため、ユーザーが存在しない場合も次の画面へ進む
      if (err.name === 'UserNotFoundException' || err.name === 'InvalidParameterException') {
        setView('reset_confirm');
      } else if (err.name === 'NotAuthorizedException') {
        setError("初回ログインがまだ完了していません。招待メールに記載の仮パスワードでログインし、パスワードを変更してください。");
      } else {
        setError(err.message || "予期せぬエラーが発生しました。");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await confirmPasswordReset(resetUsername, resetCode, resetNewPassword);
      setView('login');
      setSuccessMessage("パスワードをリセットしました。新しいパスワードでログインしてください。");
    } catch (err: any) {
      if (err.name === 'CodeMismatchException') {
        setError("確認コードが正しくありません。");
      } else if (err.name === 'ExpiredCodeException') {
        setError("確認コードの有効期限が切れています。再度リセットを行ってください。");
      } else {
        setError(err.message || "予期せぬエラーが発生しました。");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center bg-cover bg-center p-4">
      <Image
        src="/cast/images/login_bg.png"
        alt="Background"
        layout="fill"
        objectFit="cover"
        quality={100}
        className="-z-10"
      />
      <div className="w-full max-w-md">
        <Card className="bg-white/90 backdrop-blur-sm">
          <CardHeader className="items-center">
            <Image
              src="/cast/images/login_title.png"
              alt="ValueStar CTI"
              width={250}
              height={80}
              objectFit="contain"
            />
            {view !== 'login' && (
              <p className="text-lg font-semibold text-gray-700 mt-2">パスワードリセット</p>
            )}
          </CardHeader>
          <CardContent>
            {view === 'login' && (
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username">ログインID</Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="ログインIDを入力してください"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">ログインパスワード</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="パスワードを入力してください"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
                {requiresNewPassword && (
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">新しいパスワード</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      placeholder="新しいパスワードを入力してください"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      disabled={isLoading}
                    />
                  </div>
                )}
                {successMessage && (
                  <Alert className="border-green-200 bg-green-50">
                    <AlertDescription className="text-green-700">{successMessage}</AlertDescription>
                  </Alert>
                )}
                {error && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>エラー</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? "ログイン中..." : requiresNewPassword ? "パスワードを変更してログイン" : "ログイン"}
                </Button>
                {process.env.NEXT_PUBLIC_AUTH_MODE !== 'local' && (
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => { setView('reset_request'); setResetUsername(username); setError(null); setSuccessMessage(null); }}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      パスワードをお忘れですか？
                    </button>
                  </div>
                )}
              </form>
            )}

            {view === 'reset_request' && (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <p className="text-sm text-gray-600">
                  登録済みのメールアドレスを入力してください。確認コードを送信します。
                </p>
                <div className="space-y-2">
                  <Label htmlFor="resetUsername">メールアドレス</Label>
                  <Input
                    id="resetUsername"
                    type="text"
                    placeholder="メールアドレスを入力してください"
                    value={resetUsername}
                    onChange={(e) => setResetUsername(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
                {error && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>エラー</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? "送信中..." : "確認コードを送信"}
                </Button>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => { setView('login'); setError(null); }}
                    className="text-sm text-gray-500 hover:underline"
                  >
                    ログインに戻る
                  </button>
                </div>
              </form>
            )}

            {view === 'reset_confirm' && (
              <form onSubmit={handleConfirmReset} className="space-y-4">
                <p className="text-sm text-gray-600">
                  メールに届いた確認コードと新しいパスワードを入力してください。
                </p>
                <div className="space-y-2">
                  <Label htmlFor="resetCode">確認コード</Label>
                  <Input
                    id="resetCode"
                    type="text"
                    placeholder="確認コードを入力してください"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="resetNewPassword">新しいパスワード</Label>
                  <Input
                    id="resetNewPassword"
                    type="password"
                    placeholder="新しいパスワードを入力してください"
                    value={resetNewPassword}
                    onChange={(e) => setResetNewPassword(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
                {error && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>エラー</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? "リセット中..." : "パスワードをリセット"}
                </Button>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => { setView('reset_request'); setError(null); }}
                    className="text-sm text-gray-500 hover:underline"
                  >
                    戻る
                  </button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
